<script setup lang="ts">
import { components } from '~/slices'

const { client } = usePrismic()
const settings = useSettings()

const { data: page } = await useAsyncData('offerte-aanvragen', () =>
  client.getByUID('page', 'offerte-aanvragen'),
)

useDocumentSeo(page)

const form = reactive({
  firstName: '',
  lastName: '',
  honeypot: '',
  postalCode: '',
  houseNumber: '',
  street: '',
  city: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  terms: false,
})

const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submitError = ref('')

type LookupStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error'

const lookupStatus = ref<LookupStatus>('idle')
let lookupTimer: ReturnType<typeof setTimeout> | null = null
let lookupSeq = 0

function isLikelyPostcode(s: string): boolean {
  return /^\s*[1-9][0-9]{3}\s*[A-Za-z]{2}\s*$/.test(s)
}

function splitHouseNumber(value: string): { number: string; addition: string } {
  const match = value.trim().match(/^(\d+)\s*(.*)$/)
  if (!match) return { number: '', addition: '' }
  return { number: match[1] || '', addition: (match[2] || '').trim() }
}

function scheduleLookup() {
  if (lookupTimer) clearTimeout(lookupTimer)
  const { number } = splitHouseNumber(form.houseNumber)
  if (!isLikelyPostcode(form.postalCode) || !number) {
    lookupStatus.value = 'idle'
    return
  }
  lookupTimer = setTimeout(runLookup, 400)
}

async function runLookup() {
  const seq = ++lookupSeq
  const { number, addition } = splitHouseNumber(form.houseNumber)
  lookupStatus.value = 'loading'
  try {
    const res = await $fetch<{
      ok: boolean
      error?: string
      address?: { street: string; city: string }
    }>('/api/postcode-lookup', {
      query: {
        postcode: form.postalCode,
        house_number: number,
        house_addition: addition,
      },
    })
    if (seq !== lookupSeq) return
    if (res.ok && res.address) {
      form.street = res.address.street || ''
      form.city = res.address.city || form.city
      lookupStatus.value = 'found'
    } else if (res.error === 'not_found') {
      form.street = ''
      lookupStatus.value = 'not_found'
    } else if (res.error === 'not_configured') {
      lookupStatus.value = 'idle'
    } else {
      lookupStatus.value = 'error'
    }
  } catch {
    if (seq !== lookupSeq) return
    lookupStatus.value = 'error'
  }
}

watch(() => [form.postalCode, form.houseNumber], scheduleLookup)

onBeforeUnmount(() => {
  if (lookupTimer) clearTimeout(lookupTimer)
})

const lookupLine = computed(() => {
  if (lookupStatus.value === 'loading') return 'Adres ophalen…'
  if (lookupStatus.value === 'found' && form.street) {
    return `${form.street} ${form.houseNumber}, ${form.city}`
  }
  if (lookupStatus.value === 'not_found') return 'Geen adres gevonden — controleer postcode en huisnummer.'
  if (lookupStatus.value === 'error') return 'Adres ophalen mislukt — probeer het opnieuw.'
  return ''
})

function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  const w = window as unknown as { gtag?: (...args: unknown[]) => void }
  if (typeof w.gtag === 'function') {
    w.gtag('event', name, { flow: 'quote_request_form', ...params })
  }
}

async function handleSubmit() {
  submitError.value = ''
  isSubmitting.value = true
  trackEvent('quote_request_submit_attempt', { subject: form.subject })
  try {
    const { number: houseNumber, addition: houseAddition } = splitHouseNumber(form.houseNumber)
    const addressLine = [form.street, form.houseNumber].filter((p) => p && p.trim()).join(' ').trim()
    const res = await $fetch<{ ok: boolean; error?: string }>('/api/quote-request', {
      method: 'POST',
      body: {
        first_name: form.firstName,
        last_name: form.lastName,
        honeypot: form.honeypot,
        address: addressLine,
        street: form.street,
        house_number: houseNumber,
        house_addition: houseAddition,
        postal_code: form.postalCode,
        city: form.city,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        terms: form.terms,
      },
    })
    if (res.ok) {
      isSubmitted.value = true
      trackEvent('generate_lead_quote', { subject: form.subject, currency: 'EUR' })
    } else {
      submitError.value =
        res.error === 'validation'
          ? 'Niet alle verplichte velden zijn ingevuld.'
          : res.error === 'rate_limited'
          ? 'Je hebt recent al meerdere aanvragen gedaan. Probeer het later opnieuw.'
          : 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
      trackEvent('quote_request_submit_error', { reason: res.error || 'unknown' })
    }
  } catch {
    submitError.value = 'Er ging iets mis bij het versturen. Probeer het opnieuw.'
    trackEvent('quote_request_submit_error', { reason: 'request_failed' })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <main>
    <!-- Slices from Prismic -->
    <SliceZone :slices="page?.data.slices ?? []" :components="components" />

    <!-- Quote form -->
    <section class="pa-quote-page">
      <div class="pa-container">
        <div class="pa-quote-page__grid">
          <!-- Form -->
          <div class="pa-quote-page__form-wrapper">
            <div v-if="settings?.data.quote_intro" class="pa-quote-page__intro">
              <PrismicRichText :field="settings.data.quote_intro" />
            </div>

            <div v-if="isSubmitted" class="pa-quote-page__success">
              <h2>Bedankt voor uw aanvraag!</h2>
              <p>Wij nemen zo snel mogelijk contact met u op.</p>
            </div>

            <form v-else class="pa-quote-page__form" @submit.prevent="handleSubmit">
              <p class="pa-quote-page__form-required">Velden met * zijn verplicht</p>

              <!-- Honeypot — must stay empty -->
              <div class="pa-quote-page__form-honeypot" aria-hidden="true">
                <label>Website (laat leeg)</label>
                <input
                    v-model="form.honeypot"
                    type="text"
                    name="website"
                    tabindex="-1"
                    autocomplete="off"
                >
              </div>

              <!-- Persoonsgegevens -->
              <h3 class="pa-quote-page__form-section">Persoonsgegevens</h3>
              <div class="pa-quote-page__form-row">
                <div class="pa-quote-page__form-field">
                  <input v-model="form.firstName" type="text" placeholder="Voornaam*" required />
                </div>
                <div class="pa-quote-page__form-field">
                  <input v-model="form.lastName" type="text" placeholder="Achternaam*" required />
                </div>
              </div>
              <div class="pa-quote-page__form-row">
                <div class="pa-quote-page__form-field">
                  <input
                      v-model="form.postalCode"
                      type="text"
                      placeholder="Postcode*"
                      autocomplete="postal-code"
                      required
                  />
                </div>
                <div class="pa-quote-page__form-field">
                  <input
                      v-model="form.houseNumber"
                      type="text"
                      placeholder="Huisnummer*"
                      autocomplete="address-line2"
                      required
                  />
                </div>
              </div>
              <input v-model="form.street" type="hidden" name="street" />
              <input v-model="form.city" type="hidden" name="city" />
              <p
                  v-if="lookupLine"
                  class="pa-quote-page__form-lookup"
                  :class="{
                    'pa-quote-page__form-lookup--loading': lookupStatus === 'loading',
                    'pa-quote-page__form-lookup--found': lookupStatus === 'found',
                    'pa-quote-page__form-lookup--warn': lookupStatus === 'not_found' || lookupStatus === 'error',
                  }"
              >
                {{ lookupLine }}
              </p>

              <!-- Contactgegevens -->
              <h3 class="pa-quote-page__form-section">Contactgegevens</h3>
              <div class="pa-quote-page__form-row">
                <div class="pa-quote-page__form-field">
                  <input v-model="form.email" type="email" placeholder="E-mailadres*" required />
                </div>
                <div class="pa-quote-page__form-field">
                  <input v-model="form.phone" type="tel" placeholder="Telefoonnummer*" required />
                </div>
              </div>

              <!-- Subject -->
              <h3 class="pa-quote-page__form-section">Vrijblijvende offerte aanvragen?</h3>
              <div class="pa-quote-page__form-field pa-quote-page__form-field--full">
                <select v-model="form.subject" required>
                  <option value="" disabled>Vrijblijvende offerte aanvragen?*</option>
                  <option
                      v-for="(option, index) in settings?.data.quote_subject_options"
                      :key="index"
                      :value="option.label"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Message -->
              <div class="pa-quote-page__form-field pa-quote-page__form-field--full">
                <textarea v-model="form.message" placeholder="Bericht" rows="5" />
              </div>

              <!-- Terms -->
              <label class="pa-quote-page__form-checkbox">
                <input v-model="form.terms" type="checkbox" required />
                <span>Ja, ik ga akkoord met de algemene voorwaarden</span>
              </label>

              <!-- Submit -->
              <button
                  type="submit"
                  class="pa-btn pa-btn--primary"
                  :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Verzenden...' : 'Verzenden' }}
                <span v-if="!isSubmitting" aria-hidden="true">&rarr;</span>
              </button>

              <p v-if="submitError" class="pa-quote-page__form-error">{{ submitError }}</p>
            </form>
          </div>

          <ContactInfoSidebar />
        </div>
      </div>
    </section>
  </main>
</template>
